/**
 * Cuenta demo de los revisores de App Store y Google Play (comparsa `docs/CUENTAS_DEMO.md`).
 *
 * El backend deja leer a la cuenta demo pero bloquea lo que escribe (aportar, traspasar,
 * rescatar…): responde HTTP 200 con `{ success: false, errorCode: 'DEMO_ACCOUNT_READ_ONLY' }`
 * y la cabecera `X-Demo-Account-Blocked: true`. En vez de tratarlo en cada llamada de
 * `src/api`, este interceptor envuelve `fetch` UNA vez al arrancar, mira esa cabecera en las
 * respuestas de nuestra API y lanza `DEMO_ACCOUNT_BLOCKED_EVENT`, que escucha
 * `DemoAccountBlockedNotice` para enseñar el aviso.
 *
 * 🔴 No cambia nada para los clientes reales: la respuesta llega intacta y no se lee ni se
 * clona ningún cuerpo (solo se mira una cabecera).
 */

export const DEMO_ACCOUNT_READ_ONLY = 'DEMO_ACCOUNT_READ_ONLY';
export const DEMO_ACCOUNT_BLOCKED_HEADER = 'X-Demo-Account-Blocked';
export const DEMO_ACCOUNT_BLOCKED_EVENT = 'arca:demo-account-blocked';

let installed = false;
let noticeOpen = false;

/**
 * ¿Está abierto el aviso de la cuenta demo? Se marca de forma SÍNCRONA al detectar el bloqueo,
 * antes de devolver la respuesta: una pantalla que pinta su propio aviso de error puede
 * consultarlo y callarse sin carreras con el render de `DemoAccountBlockedNotice`.
 */
export const isDemoAccountNoticeOpen = (): boolean => noticeOpen;

/** Lo llama `DemoAccountBlockedNotice` al cerrarse. */
export const closeDemoAccountNotice = (): void => {
	noticeOpen = false;
};

const urlOf = (input: RequestInfo | URL): string => {
	if (typeof input === 'string') return input;
	if (input instanceof URL) return input.href;
	return input.url;
};

const isOwnApi = (url: string): boolean => {
	const api = process.env.REACT_APP_BASE_API;
	return Boolean(api) && url.startsWith(api as string);
};

export const installDemoAccountInterceptor = (): void => {
	// Sin `fetch` el navegador no está soportado (lo dice `AvisoNavegador`): no romper el
	// arranque, que dejaría la página en blanco en vez del aviso.
	if (installed || typeof window.fetch !== 'function') return;
	installed = true;

	const originalFetch = window.fetch.bind(window);
	window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const response = await originalFetch(input, init);
		if (response.headers.get(DEMO_ACCOUNT_BLOCKED_HEADER) === 'true' && isOwnApi(urlOf(input))) {
			noticeOpen = true;
			window.dispatchEvent(new CustomEvent(DEMO_ACCOUNT_BLOCKED_EVENT));
		}
		return response;
	};
};

/**
 * ¿Es el bloqueo de la cuenta demo? Para que una pantalla NO pinte su propio error encima
 * del aviso único: acepta el `Error` que lanza `parseArcaResponse` o la respuesta que
 * devuelve una función de `src/api`.
 */
export const isDemoAccountBlocked = (value: unknown): boolean => {
	if (value instanceof Error) return value.message === DEMO_ACCOUNT_READ_ONLY;
	if (typeof value !== 'object' || value === null) return false;
	const { errorCode, message } = value as { errorCode?: unknown; message?: unknown };
	return errorCode === DEMO_ACCOUNT_READ_ONLY || message === DEMO_ACCOUNT_READ_ONLY;
};

/** Solo para los tests: permite volver a instalarlo sobre un `fetch` simulado nuevo. */
export const resetDemoAccountInterceptorForTests = (): void => {
	installed = false;
	noticeOpen = false;
};
